import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  Avatar,
  Popover,
  TextButton,
  Divider,
  Layout,
  Cell,
} from 'wix-style-react';
import { ChevronDown } from '@wix/wix-ui-icons-common';
import { marketplaceHeaderClientGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useBi } from '../../Hooks/useBi';
import {
  dataHooks,
  popoverIds,
  myProjectsNewPath,
  contactSupportUrl,
  accountSettingsUrl,
  wixUrl,
} from '../utils/constants';
import LanguageSelector from '../LanguageSelector';
import CurrencySelector from '../CurrencySelector';
import { CurrencySelectorProps } from '../CurrencySelector/CurrencySelector';
import LogoutBtn from './LogoutBtn';
import s from './LoginArea.module.scss';

const isBlankImage = (src: string = '') =>
  RegExp('https://gravatar.com/avatar/.*\\?d=blank').test(src);
export interface LoggedinAreaProps {
  keys?: any;
  language: string;
  userProfileImg?: string;
  userData?: any;
  currencySelectorProps?: Omit<CurrencySelectorProps, 'localeDataTranslations'>;
  localeDataTranslations: Record<string, any>;
  pageName: string;
  isLess1000View: boolean;
  marketplaceUrlPrefix: string;
}
const LoggedinArea: FC<LoggedinAreaProps> = ({
  keys,
  language = 'en',
  userProfileImg,
  userData,
  currencySelectorProps,
  localeDataTranslations,
  pageName,
  isLess1000View,
  marketplaceUrlPrefix,
}) => {
  const shouldShowCurrencySelector = currencySelectorProps;
  const [shown, setShown] = useState<boolean>(false);
  const logger = useBi();

  function focusContent() {
    const interval: any = setInterval(() => {
      if (
        document.querySelector(`#${popoverIds.loggedinPopoverContent} button`)
      ) {
        clearInterval(interval);
        (
          document.querySelector(
            `#${popoverIds.loggedinPopoverContent} button`,
          ) as HTMLElement
        )?.focus();
      }
    }, 100);
  }

  useEffect(() => {
    if (isLess1000View) {
      setShown(false);
    }
  }, [isLess1000View]);

  useEffect(() => {
    if (shown) {
      focusContent();
    }
  }, [shown]);

  return (
    <Box dataHook={dataHooks.loggedinArea}>
      <Popover
        appendTo="window"
        moveBy={{ x: -60, y: 30 }}
        placement="bottom"
        shown={shown}
        showArrow={true}
        onClick={() => setShown(!shown)}
        onClickOutside={(e: any) => {
          const isInLanguagePopover = !!e.target.closest(
            `#${popoverIds.languagePopover}`,
          );
          const isInCurrencyPopover = !!e.target.closest(
            `#${popoverIds.currencyPopover}`,
          );
          if (!isInLanguagePopover && !isInCurrencyPopover) {
            setShown(false);
          }
        }}
        disableClickOutsideWhenClosed={true}
        id={popoverIds.loggedinPopover}
        dataHook="loggedin-popover-menu"
      >
        <Popover.Element>
          <TextButton
            dataHook="loggedin-popover-trigger"
            skin="dark"
            size="medium"
            weight="normal"
            suffixIcon={<ChevronDown />}
          >
            <Avatar
              size="size30"
              name={userData?.userName}
              imgProps={{
                src: !isBlankImage(userProfileImg) ? userProfileImg : undefined,
              }}
            ></Avatar>
          </TextButton>
        </Popover.Element>
        <Popover.Content>
          <div
            style={{ padding: '30px 0 36px' }}
            id={popoverIds.loggedinPopoverContent}
            onKeyDown={(e: any) => {
              if (e.keyCode === 27) {
                setShown(false);
                (
                  document.querySelector(
                    `#${popoverIds.loggedinPopover} button`,
                  ) as HTMLElement
                )?.focus();
              }
            }}
          >
            <Layout gap="12px 0">
              <Cell span={12}>
                <Box padding="0 SP2">
                  <TextButton
                    dataHook={dataHooks.myProjectsLink}
                    skin="dark"
                    size="medium"
                    weight="thin"
                    className={s.menuLink}
                    onClick={() => {
                      logger.report(
                        marketplaceHeaderClientGenericClick({
                          clickType: 'click in profile drop down menu',
                          click_data: 'my projects',
                          pageName,
                        }),
                      );
                      window.open(
                        `${marketplaceUrlPrefix}${myProjectsNewPath}`,
                        '_self',
                      );
                    }}
                  >
                    {keys.myProjects}
                  </TextButton>
                </Box>
              </Cell>
              <Cell span={12}>
                <Box padding="SP1 SP4" minWidth="156px">
                  <Divider />
                </Box>
              </Cell>
              <Cell span={12}>
                <Box padding="0 SP2">
                  <TextButton
                    dataHook={dataHooks.contactSupportLink}
                    skin="dark"
                    size="medium"
                    weight="thin"
                    className={s.menuLink}
                    onClick={() => {
                      logger.report(
                        marketplaceHeaderClientGenericClick({
                          clickType: 'click in profile drop down menu',
                          click_data: 'contact support',
                          pageName,
                        }),
                      );
                      window.open(contactSupportUrl, '_blank');
                    }}
                  >
                    {keys.contactSupport}
                  </TextButton>
                </Box>
                <Box padding="0 SP2">
                  <TextButton
                    dataHook={dataHooks.accountSettingsLink}
                    skin="dark"
                    size="medium"
                    weight="thin"
                    className={s.menuLink}
                    onClick={() => {
                      logger.report(
                        marketplaceHeaderClientGenericClick({
                          clickType: 'click in profile drop down menu',
                          click_data: 'account settings',
                          pageName,
                        }),
                      );
                      window.open(accountSettingsUrl, '_blank');
                    }}
                  >
                    {keys.accountSettings}
                  </TextButton>
                </Box>
              </Cell>
              <Cell span={12}>
                <Box padding="SP1 SP4">
                  <Divider />
                </Box>
              </Cell>
              <Cell span={12}>
                <Box>
                  <LanguageSelector
                    value={language}
                    pageName={pageName}
                    type="loggedIn"
                  />
                </Box>
                {shouldShowCurrencySelector ? (
                  <Box padding="SP1 SP3">
                    <CurrencySelector
                      value={currencySelectorProps!.value}
                      onChange={(option) => {
                        currencySelectorProps!.onChange(option);
                        setShown(false);
                      }}
                      localeDataTranslations={localeDataTranslations}
                      isLess1000View={isLess1000View}
                      type="loggedIn"
                    />
                  </Box>
                ) : null}
              </Cell>
              <Cell span={12}>
                <Box padding="SP1 SP4">
                  <Divider />
                </Box>
              </Cell>
              <Cell span={12}>
                <Box padding="0 SP2">
                  <TextButton
                    dataHook={dataHooks.wixLink}
                    skin="dark"
                    size="medium"
                    weight="thin"
                    className={s.menuLink}
                    onClick={() => {
                      logger.report(
                        marketplaceHeaderClientGenericClick({
                          clickType: 'click in profile drop down menu',
                          click_data: 'go to wix',
                          pageName,
                        }),
                      );
                      window.open(wixUrl, '_blank');
                    }}
                  >
                    {keys.wix}
                  </TextButton>
                </Box>
              </Cell>
              <Cell span={12}>
                <Box padding="SP2 0 0 SP5">
                  <LogoutBtn
                    btnLogoutLabel={keys.btnLogout}
                    pageName={pageName}
                  />
                </Box>
              </Cell>
            </Layout>
          </div>
        </Popover.Content>
      </Popover>
    </Box>
  );
};

export default LoggedinArea;
